import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const ROLES = [
  { key: 'owner', label: 'Owner', description: 'Full platform access' },
  { key: 'admin', label: 'Admin', description: 'Full access except billing' },
  { key: 'manager', label: 'Manager', description: 'Operations and limited finance' },
  { key: 'technician', label: 'Technician', description: 'Field operations only' },
  { key: 'salesperson', label: 'Salesperson', description: 'Sales and customer management' },
  { key: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

const PERMISSION_GROUPS = [
  {
    label: 'Menu Visibility',
    icon: 'Layout',
    permissions: [
      { key: 'can_view_dashboard', label: 'Dashboard' },
      { key: 'can_view_my_dashboard', label: 'My Dashboard' },
      { key: 'can_view_sales', label: 'Sales Menu' },
      { key: 'can_view_operations', label: 'Operations Menu' },
      { key: 'can_view_finance', label: 'Finance Menu' },
      { key: 'can_view_reports', label: 'Reports Menu' },
      { key: 'can_view_data_management', label: 'Data Management' },
      { key: 'can_view_settings', label: 'Settings Menu' },
    ],
  },
  {
    label: 'Ticket Actions',
    icon: 'ClipboardList',
    permissions: [
      { key: 'can_create_tickets', label: 'Create Tickets' },
      { key: 'can_edit_tickets', label: 'Edit Tickets' },
      { key: 'can_delete_tickets', label: 'Delete Tickets' },
    ],
  },
  {
    label: 'Sales Actions',
    icon: 'FileText',
    permissions: [
      { key: 'can_create_quotes', label: 'Create Quotes' },
      { key: 'can_approve_quotes', label: 'Approve Quotes' },
      { key: 'can_create_invoices', label: 'Create Invoices' },
    ],
  },
  {
    label: 'Management Actions',
    icon: 'Settings',
    permissions: [
      { key: 'can_manage_personnel', label: 'Manage Personnel' },
      { key: 'can_manage_customers', label: 'Manage Customers' },
      { key: 'can_manage_inventory', label: 'Manage Inventory' },
      { key: 'can_manage_billing', label: 'Manage Billing' },
    ],
  },
];

const RolePermissionsSettings = () => {
  const { organizationId, isOrgAdmin, isSuperAdmin } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedRole, setSavedRole] = useState(null);
  const [error, setError] = useState(null);
  const [activeRole, setActiveRole] = useState('manager');

  useEffect(() => {
    if (organizationId) fetchPermissions();
  }, [organizationId]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase?.from('role_permissions')?.select('*')?.eq('organization_id', organizationId);

      if (err) throw err;

      const permMap = {};
      data?.forEach(p => { permMap[p.role] = p; });
      setPermissions(permMap);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (role, permKey) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev?.[role],
        [permKey]: !prev?.[role]?.[permKey],
      },
    }));
  };

  const handleSave = async (role) => {
    if (!organizationId) return;
    setSaving(true);
    setError(null);
    try {
      const rolePerms = permissions?.[role];
      const { error: err } = await supabase?.from('role_permissions')?.upsert(
          { ...rolePerms, organization_id: organizationId, role },
          { onConflict: 'organization_id,role' }
        );
      if (err) throw err;
      setSavedRole(role);
      setTimeout(() => setSavedRole(null), 2000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const currentPerms = permissions?.[activeRole] ?? {};

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-6 px-4 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="ShieldCheck" size={18} color="#2563eb" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Role Permissions</h1>
          </div>
          <p className="text-muted-foreground text-sm">Configure menu visibility and action permissions for each role in your organization</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Role Selector */}
            <div className="w-48 flex-shrink-0">
              <div className="bg-card rounded-xl shadow-elevation-1 overflow-hidden">
                {ROLES?.map(role => (
                  <button
                    key={role?.key}
                    onClick={() => setActiveRole(role?.key)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-0 transition-smooth ${
                      activeRole === role?.key
                        ? 'bg-primary/10 text-primary font-medium' :'text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="text-sm font-medium">{role?.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{role?.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Permissions Panel */}
            <div className="flex-1">
              <div className="bg-card rounded-xl shadow-elevation-1 p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-lg font-heading font-semibold text-foreground">
                      {ROLES?.find(r => r?.key === activeRole)?.label} Permissions
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {ROLES?.find(r => r?.key === activeRole)?.description}
                    </p>
                  </div>
                  {(activeRole === 'owner' || activeRole === 'admin') ? (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      Full Access
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSave(activeRole)}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                    >
                      {savedRole === activeRole ? (
                        <><Icon name="Check" size={14} color="currentColor" /> Saved!</>
                      ) : (
                        <><Icon name="Save" size={14} color="currentColor" /> Save Changes</>
                      )}
                    </button>
                  )}
                </div>

                {(activeRole === 'owner' || activeRole === 'admin') ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="ShieldCheck" size={40} color="#7c3aed" />
                    <p className="mt-3 font-medium">Owner and Admin roles always have full access</p>
                    <p className="text-sm mt-1">These roles cannot be restricted</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {PERMISSION_GROUPS?.map(group => (
                      <div key={group?.label}>
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name={group?.icon} size={16} color="var(--color-muted-foreground)" />
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            {group?.label}
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {group?.permissions?.map(perm => (
                            <label
                              key={perm?.key}
                              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 cursor-pointer transition-smooth"
                            >
                              <div
                                onClick={() => handleToggle(activeRole, perm?.key)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                                  currentPerms?.[perm?.key] ? 'bg-primary' : 'bg-gray-300'
                                }`}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                  currentPerms?.[perm?.key] ? 'translate-x-4' : 'translate-x-1'
                                }`} />
                              </div>
                              <span className="text-sm text-foreground">{perm?.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RolePermissionsSettings;
